import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { useAuth } from '@/auth/useAuth'

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-line bg-surface p-5 ${className}`}>{children}</div>
  )
}

export function PageHeader({
  title,
  task,
  children,
}: {
  title: string
  /** The work plan task or requirement this screen answers. */
  task?: string
  children?: ReactNode
}) {
  return (
    <header className="mb-6 border-b border-line pb-4">
      {task && <p className="ref mb-1">{task}</p>}
      <h1 className="text-2xl text-pounamu">{title}</h1>
      {children && <p className="mt-2 max-w-2xl text-sm text-inksoft">{children}</p>}
    </header>
  )
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'quiet'
}

export function Button({ variant = 'primary', className = '', ...props }: ButtonProps) {
  const styles = {
    primary: 'bg-pounamu text-white hover:bg-pounamulight disabled:bg-inksoft',
    secondary: 'border border-pounamu text-pounamu hover:bg-pounamuwash',
    quiet: 'text-inksoft hover:text-ink underline underline-offset-4',
  }[variant]

  return (
    <button
      className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${styles} ${className}`}
      {...props}
    />
  )
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'good' | 'locked' | 'alert'
}) {
  const styles = {
    neutral: 'bg-paper text-inksoft border-line',
    good: 'bg-pounamuwash text-pounamu border-pounamu/25',
    locked: 'bg-goldwash text-gold border-gold/30',
    alert: 'bg-alertwash text-alert border-alert/25',
  }[tone]

  return (
    <span className={`inline-block rounded-sm border px-2 py-0.5 text-xs font-medium ${styles}`}>
      {children}
    </span>
  )
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-line bg-surface px-6 py-10 text-center">
      <h2 className="text-base text-ink">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-inksoft">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="rounded-sm border border-alert/30 bg-alertwash px-3 py-2 text-sm text-alert">
      {children}
    </p>
  )
}

/**
 * The data boundary bar.
 *
 * It sits above anything that reads or writes evaluation data and names, in
 * plain words, whose records the screen can reach. R39 and the partner data
 * agreement (5.4) are promises made to partners about data they cannot see;
 * this is the one place the promise is visible while they work. It reads from
 * the same session the queries use, so if it ever named the wrong
 * organisation, the queries would be wrong too.
 */
export function DataBoundaryBar() {
  const { profile, isKtm } = useAuth()
  const org = profile?.organisations

  if (isKtm) {
    return (
      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-sm border border-gold/30 bg-goldwash px-3 py-2 text-sm text-gold">
        <span className="ref text-gold">Aggregate view</span>
        <span>You are seeing data across every partner. Partners cannot see each other.</span>
      </div>
    )
  }

  if (!org) return null

  return (
    <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-sm border border-pounamu/20 bg-pounamuwash px-3 py-2 text-sm text-pounamu">
      <span className="ref text-pounamu">Your data only</span>
      <span>
        Everything here belongs to {org.name}. No other partner can open, search or export it.
      </span>
    </div>
  )
}
